import { useMemo } from 'react';
import { UseQueryResult, useQuery } from '@tanstack/react-query';

import { Campaign, CampaignAdGroup, CampaignAdGroupAudienceSource } from '../../../types/Campaign';
import { DspAudience as DspAudienceSource } from '../../../types/DspAudience';
import ApiService from '../../../ApiService';

interface UseProps {
    campaign: Campaign;
}

interface UseState {
    targetQuery: UseQueryResult<DspAudienceSource[]>;
    targetCount: number;
}

const useCampaignAdGroupAudienceSourceSummary = ({ campaign }: UseProps): UseState => {
    const targetIds = useMemo(() => {
        return [
            ...new Set(
                campaign.adGroups
                    ?.flatMap((adGroup: CampaignAdGroup) => adGroup.adGroupAudienceSources || [])
                    .map((adGroupTarget: CampaignAdGroupAudienceSource) => adGroupTarget.dspAudienceId)
            ),
        ];
    }, [campaign.adGroups]);

    const targetQuery = useQuery<DspAudienceSource[]>({
        queryKey: ['campaign/adGroupAudienceSourceSummary', targetIds],
        queryFn: async () => {
            const { data } = await ApiService.getDspAudienceSources({
                ids: targetIds,
            });

            return data;
        },
        enabled: !!targetIds.length,
        refetchOnWindowFocus: false,
    });

    const targetCount = useMemo((): number => {
        return targetQuery.data?.length || 0;
    }, [targetQuery.data]);

    return { targetQuery, targetCount };
};

export type { UseProps as CampaignAdGroupAudienceSourceSummaryProps };
export type { UseState as CampaignAdGroupAudienceSourceSummaryState };
export { useCampaignAdGroupAudienceSourceSummary };
